// Qué renglones entran en un descuento "para servicios".
// Mismo criterio que server/utils/discountCalc.js (serviceMatchesList).
//
// - Excluidos (excluded_services): "todos los servicios menos estos", por
//   nombre. Los servicios nuevos que se carguen entran solos (ej: Autopista
//   20% sin Mano de obra).
// - Lista específica (eligible_services): manda el NOMBRE, aunque el renglón
//   haya quedado grabado como producto.
// - Sin lista: todos los servicios, con el criterio de tipo de cada pantalla.

const listaDeNombres = (v: any): string[] => {
  let arr: any[] = [];
  if (Array.isArray(v)) arr = v;
  else if (typeof v === 'string' && v) {
    try { const p = JSON.parse(v); if (Array.isArray(p)) arr = p; } catch { /* sin lista */ }
  }
  return arr.map((s) => String(s || '').trim()).filter(Boolean);
};

export const serviciosElegibles = (discount: any): string[] => listaDeNombres(discount?.eligible_services);
export const serviciosExcluidos = (discount: any): string[] => listaDeNombres(discount?.excluded_services);

const nombresItem = (item: any): string[] =>
  [item?.name, item?.description].map((n) => String(n || '').trim()).filter(Boolean);

export const esServicioExcluido = (item: any, excluidos: string[]): boolean =>
  excluidos.length > 0 && nombresItem(item).some((n) => excluidos.includes(n));

export const entraEnDescuentoDeServicios = (
  item: any,
  elegibles: string[],
  esServicio: (item: any) => boolean,
  excluidos: string[] = []
): boolean => {
  if (esServicioExcluido(item, excluidos)) return false;
  if (elegibles.length === 0) return esServicio(item);
  return nombresItem(item).some((n) => elegibles.includes(n));
};

// ---------------------------------------------------------------------------
// Ítems libres (sin artículo del catálogo): por defecto NO llevan descuento;
// la casilla del renglón (item.applyDiscount) lo aplica o lo quita.
// Espejo de server/utils/discountCalc.js + server/utils/itemsCatalogo.js
// ---------------------------------------------------------------------------

export const esItemLibre = (item: any): boolean => !String(item?.productId || '').trim();

// Casilla "aplicar descuento": true / false / null (nadie la tocó)
export const decisionManual = (item: any): boolean | null => {
  const v = item?.applyDiscount;
  if (v === true || v === 1 || v === '1' || v === 'true') return true;
  if (v === false || v === 0 || v === '0' || v === 'false') return false;
  return null;
};

// Separa lo que el descuento toma solo (catálogo, casilla sin tocar) de lo que
// alguien tildó a mano (entra siempre, sea cual sea su tipo)
export const separarPorDecision = <T,>(items: T[]): { automaticos: T[]; tildados: T[] } => ({
  automaticos: (items || []).filter((it) => decisionManual(it) === null && !esItemLibre(it)),
  tildados: (items || []).filter((it) => decisionManual(it) === true)
});

// ¿El renglón lleva descuento? (para mostrarlo en la tabla)
export const renglonConDescuento = (item: any, entraPorRegla: (item: any) => boolean): boolean => {
  const m = decisionManual(item);
  if (m !== null) return m;
  if (esItemLibre(item)) return false;
  return entraPorRegla(item);
};

export const normalizarNombre = (s: any): string => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

type ProductoCatalogo = { id: string; name: string; category?: string; isService?: boolean };
const esServicioDeCatalogo = (p: ProductoCatalogo) => p.isService === true || String(p.category || '').toLowerCase() === 'servicio';

// Busca el artículo del catálogo de un renglón: por productId, o por nombre
// EXACTO sin tildes/mayúsculas/espacios (nada de "contiene": "Bieletas de
// barra..." no es "Bieleta"). Con nombres repetidos de distinto tipo no adivina.
export const buscarEnCatalogo = <P extends ProductoCatalogo>(item: any, productos: P[]): P | null => {
  const lista = productos || [];
  if (item?.productId) {
    const porId = lista.find((p) => String(p.id) === String(item.productId));
    if (porId) return porId;
  }
  for (const nombre of [item?.name, item?.description]) {
    const k = normalizarNombre(nombre);
    if (!k) continue;
    const candidatos = lista.filter((p) => normalizarNombre(p.name) === k);
    if (candidatos.length === 1) return candidatos[0];
    if (candidatos.length > 1) {
      const exacto = candidatos.find((p) => String(p.name || '').trim() === String(nombre || '').trim());
      if (exacto) return exacto;
      const tipo = esServicioDeCatalogo(candidatos[0]);
      return candidatos.every((p) => esServicioDeCatalogo(p) === tipo) ? candidatos[0] : null;
    }
  }
  return null;
};

// Engancha los renglones al catálogo: el enganchado toma productId y el tipo
// real (respetando el idioma del renglón). Sin catálogo cargado no toca nada.
// Un productId que no aparece en la lista se deja como está (la lista del
// front podría estar incompleta); las referencias rotas las limpia el server.
export const vincularConCatalogo = <T extends Record<string, any>>(items: T[], productos: ProductoCatalogo[]): T[] => {
  if (!Array.isArray(items) || !productos || productos.length === 0) return items;
  return items.map((it) => {
    if (!it || typeof it !== 'object') return it;
    const p = buscarEnCatalogo(it, productos);
    if (!p) return it;
    const t = String(it.type || '').toLowerCase();
    const enCastellano = t === 'servicio' || t === 'producto';
    const serv = esServicioDeCatalogo(p);
    const type = serv ? (enCastellano ? 'servicio' : 'service') : (enCastellano ? 'producto' : 'product');
    return (it.productId === p.id && it.type === type) ? it : { ...it, productId: p.id, type };
  });
};
